import { DatasetService, EnvironmentVariables, ProductPropertyValue, ProductWithContext } from '../dataset.js';
import { Schema } from '../schema.js';
import { Product, ProductGroup } from '../product.js';
import { Option, OptionGroup, SourcedValue } from '../option.js';
import { productTypeGuard, RecordKind } from '../record.js';
import {
  EnumType,
  ListType,
  ObjectType,
  PrimitiveType,
  Property,
  PropertyOptions,
  SystemType,
  TypeKind,
  TypeReference,
  UnionType
} from '../types.js';
import { createExpressionConstraint, ExpressionConstraint } from '../constraint.js';
import { PropertyPathWalker } from '../internal/PropertyPathWalker.js';
import { IPlugin, PluginBase } from '../plugin.js';
import deepEqual from 'deep-equal';
import { PrototypeTraverser, SimpleVisitor, ValidationVisitor } from '../internal/PrototypeVisitor.js';
import { engineUtils } from '../services/EngineUtils.js';
import { OclConstraintRegistry } from '../services/OclConstraintRegistry.js';
import { ExternalFieldRegistry } from '../services/ExternalFieldRegistry.js';
import { PrimitiveValidator } from '../validators/PrimitiveValidator.js';
import { ExternalParser } from '../parsers/ExternalParser.js';
import { PropertyPathParser } from '../parsers/PropertyPathParser.js';
import { PrimitiveParser } from '../parsers/PrimitiveParser.js';
import { FilterRegistry } from '../services/FilterRegistry.js';
import { CompatibilityTable } from '../compatibility/CompatibilityTable.js';
import { CompatibilityTablesDescriptorParser } from './CompatibilityTablesDescriptorParser.js';
import { PModel, PropertyPath } from '../engine.js';
import { ISearchCriterion } from './SearchCriteriaBuilder.js';
import IProductTransformer, {
  FlatProductTransformer,
  ProductTransformerResultType,
  ProductTransformerValue
} from '../services/transformers/ProductTransformer.js';
import { ResolutionSchemaParser } from '../parsers/ResolutionSchemaParser.js';
import { SchemaError } from '../errors/SchemaError.js';
import { NullYamlTransformer, YamlTransformer } from './YamlTransformer.js';
import { OptionsRegistry } from './OptionsRegistry.js';
import { Doc } from './visitors/DataFile.js';
import { CompositeDataFileVisitor } from './visitors/CompositeDataFileVisitor.js';
import { TransformingDataFileVisitor } from './visitors/TransformingDataFileVisitor.js';
import { ParsingDataFileVisitor } from './visitors/ParsingDataFileVisitor.js';
import { PostprocessingProductVisitor } from './visitors/PostprocessingProductVisitor.js';
import { DatasetList } from './visitors/DatasetList.js';
import { OptionDefinition } from './yaml-models.js';
import { ModelChecker } from '../internal/ModelChecker.js';
import { IDataFileVisitor } from './visitors/IDataFileVisitor.js';

export type Products = { [k: string]: Product | ProductGroup }
type CompatibilityTableName = string;

interface PluginModule {
  default: IPlugin;
}

interface PluginRegistry {
  [k: string]: PluginModule;
}

function isPropertyOptionsEqual(opts: PropertyOptions, opts2: PropertyOptions) {
  return deepEqual(opts, opts2);
}

function isPropertiesIncompatible(property: Property, componentProperty: Property, ignoreDefault = false) {
  let aOpts;
  let bOpts;
  if (ignoreDefault) {
    const { default: componentValue, ...propOpts } = property.opts;
    aOpts = propOpts;
    const { default: objectValue, ...componentOpts } = componentProperty.opts;
    bOpts = componentOpts;
  } else {
    aOpts = property.opts;
    bOpts = componentProperty.opts;
  }
  return property.name === componentProperty.name &&
    (
      property.type !== componentProperty.type ||
      !isPropertyOptionsEqual(aOpts, bOpts)
    );
}

function overrideProperties(typeRef: TypeReference, component: Property[], object: Property[]) {
  const map = new Map<string, Property>();
  component.forEach(property => map.set(property.name, property));
  object.forEach(objectProp => {
    // Object property can override component default value
    if (map.has(objectProp.name)) {
      const componentProp = map.get(objectProp.name);
      if (isPropertiesIncompatible(componentProp, objectProp, true)) {
        throw new Error(`Wrong type definition for ${typeRef}. Cannot override property ${objectProp.name}`);
      }
    }
    map.set(objectProp.name, objectProp);
  });
  return Array.from(map.values());
}

function castProduct(product: Product | ProductGroup): Product {
  if (!product || product.kind !== 'Product') {
    throw new Error('Invalid product');
  }
  return product as Product;
}

type CompatibilityTablesRegistry = Map<CompatibilityTableName, CompatibilityTable>;

export class AsyncJsDatasetService implements DatasetService {
  protected schema: Schema;

  protected optionsRegistry = new OptionsRegistry();

  public products: Products = {};
  public constraintRegistry: OclConstraintRegistry;
  public externalFieldRegistry: ExternalFieldRegistry;
  protected typesMap: Map<TypeReference, any>;
  protected primitiveValidator: PrimitiveValidator;
  protected externalParser: ExternalParser;
  protected propertyPathValidator: PropertyPathParser;
  protected primitiveParser: PrimitiveParser;
  protected filterRegistry: FilterRegistry;
  public compatibilityTables: CompatibilityTablesRegistry = new Map<CompatibilityTableName, CompatibilityTable>();
  private visitor: CompositeDataFileVisitor;
  protected _datasetList: DatasetList;

  protected constructor() {
  }


  static async createService(schema: any,
                             data: any[] = [],
                             transformer: YamlTransformer = new NullYamlTransformer()): Promise<AsyncJsDatasetService> {
    const datasetService = new AsyncJsDatasetService();
    await datasetService.init(schema, data, [], transformer);
    return datasetService;
  }

  addJsonDatasets(datasets: Doc[][]): void {
    const dataFileList = new DatasetList(datasets);
    dataFileList.accept(this.visitor);
  }

  addDatasets(datasets: Doc[][]): void {
    this.addJsonDatasets(datasets);
  }

  protected async init(loadedSchema: any,
                       dataFiles: any[] = [],
                       plugins: (string | PluginBase)[] = [],
                       transformer: YamlTransformer = new NullYamlTransformer()) {

    if (loadedSchema.kind !== RecordKind.Schema) {
      throw new Error('Illegal schema file');
    }
    this.schema = new Schema();
    this.typesMap = new Map<TypeReference, any>();
    this.constraintRegistry = new OclConstraintRegistry(this.schema);
    this.filterRegistry = new FilterRegistry(this.schema);
    this.externalFieldRegistry = new ExternalFieldRegistry(this.schema);
    this.primitiveValidator = new PrimitiveValidator(this.schema);
    this.primitiveParser = new PrimitiveParser(this.primitiveValidator);
    this.propertyPathValidator = new PropertyPathParser(this.schema);
    this.externalParser = new ExternalParser(this.primitiveValidator, this.propertyPathValidator);
    const languages = loadedSchema.languages || [];
    this.schema.addLanguageSupport(...languages);

    // Build index by typeReference
    for (let typesKey in loadedSchema.types) {
      let type = loadedSchema.types[typesKey];
      if (this.typesMap.has(type.name)) {
        throw new Error(`Wrong schema: type ${type.name} is duplicated`);
      }
      this.typesMap.set(type.name, type);
    }

    for (let type of this.typesMap.values()) {
      this.registerType(type);
    }

    for (let type of Object.values(this.schema.types)) {
      if (type.kind === TypeKind.Object) {
        this.parseExternals(type);
      }
    }

    this.visitor = new CompositeDataFileVisitor(
      new TransformingDataFileVisitor(transformer),
      new ParsingDataFileVisitor(
        this.optionsRegistry,
        this.products,
        this.constraintRegistry,
        this.filterRegistry,
        this.externalParser,
        this.externalFieldRegistry,
        this.schema,
        this.compatibilityTables
      ),
      new PostprocessingProductVisitor(transformer)
    );


    this._datasetList = new DatasetList(dataFiles);
    this._datasetList.accept(this.visitor);

    const pluginRegistry: PluginRegistry = {};
    for (let plugin of plugins) {
      if (typeof plugin === 'string') {
        eval(`(function(window) {
          ${plugin}
        })`)(pluginRegistry);
      } else {
        this.schema.registerPlugin(plugin);
        console.log('PluginBase detected!');
      }
    }

    for (let value of Object.values(pluginRegistry)) {
      const plugin = new (value.default as any)();
      this.schema.registerPlugin(plugin);
    }

    this.parseResolutionSchema(loadedSchema);

    this.schema.validate();
    this.validateProducts();
  }

  splitProducts(): [ProductGroup[], Product[]] {
    const products: Product[] = [];
    const productGroups: ProductGroup[] = [];

    for (const record of Object.values(this.products)) {
      if (productTypeGuard(record)) {
        products.push(record);
      } else {
        productGroups.push(record);
      }
    }
    return [productGroups, products];
  }

  getExternalFieldRegistry(typeRef: TypeReference): ExternalFieldRegistry {
    return this.externalFieldRegistry;
  }

  findOneProductInProductGroupByExample(productGroupName: ProductGroup, example: { [p: string]: any }): Product | undefined {
    let products = engineUtils.flattenProducts(productGroupName);
    for (let product of products) {
      let match = true;
      for (let key in example) {
        if (example[key] && product.model[key] !== example[key]) {
          match = false;
          break;
        }
      }
      if (match) {
        return product;
      }
    }
    return undefined;
  }

  findProducts<T extends ProductTransformerValue>(typeRef: TypeReference, transformer: IProductTransformer<T>, criteria?: ISearchCriterion): ProductTransformerResultType<IProductTransformer<T>> {
    let result = [];

    this.validateTypeRef(typeRef);

    const [groups, products] = this.splitProducts();

    // @todo: wrap validation result in a class
    if (criteria) {
      const validationResult = criteria.validate(typeRef, this.schema);
      if (validationResult.length > 0) {
        for (const error of validationResult) {
          console.warn(`Invalid criterion: ${error}`);
        }
        return transformer.transform(result, products, groups);
      }
    }

    for (const product of Object.values(this.products)) {
      if (productTypeGuard(product)) {
        if (product.type.typeRef === typeRef && (!criteria || criteria.meets(product.model))) {
          result.push(product);
        }
      }
    }
    return transformer.transform(result, products, groups);
  }

  availableProductPropertyValues(typeRef: TypeReference, propertyPath: PropertyPath, criteria?: ISearchCriterion): ProductPropertyValue[] {
    const result = new Set<ProductPropertyValue>();
    const visitedOptionsId = new Set<string>();
    const products = this.findProducts(typeRef, new FlatProductTransformer(), criteria);

    for (const product of products) {
      const propertyValue = new PropertyPathWalker(propertyPath, this.schema, product.model, typeRef).getPropertyValue();
      if (propertyValue !== undefined) {
        const { value } = propertyValue;

        if (value instanceof SourcedValue) {
          if (Array.isArray(value.value)) {
            throw new Error('Array options are not supported');
          }

          const optionId = value.value._id;

          if (!visitedOptionsId.has(optionId)) {
            result.add(value.value);
            visitedOptionsId.add(optionId);
          }
        } else {
          if (Array.isArray(value)) {
            for (const item of value) {
              result.add(item);
            }
          } else if (typeof value === 'object') {
            throw new Error('Not a primitive type');
          } else {
            result.add(value);
          }
        }
      }
    }

    return [...result];
  }

  /**
   * @deprecated
   * @param datasetName
   * @param options
   * @param typeName
   */
  getOptionGroupByType(datasetName: string, options: EnvironmentVariables, typeName: string): OptionGroup[] {
    const groups = this.optionsRegistry.getOptionGroups().map(group => ({
      ...group,
      children: (group as OptionGroup).children.filter(o => o.type.typeRef === typeName)
    }))
      .filter(group => group.children.length > 0);
    return groups;
  }

  /**
   * @deprecated
   * **/
  getOptionsByType(datasetName: string, options: EnvironmentVariables, productName: string, typeName: string[]): { [p: string]: OptionGroup[] | Option[] | String[] | Number[] } {
    return {};
  }

  getProductGroupsTree(datasetName: string, options: EnvironmentVariables): ProductGroup {
    return this.products.ROOT as ProductGroup;
  }

  getProductGroup(productGroupName: string): ProductGroup | undefined {
    return this.products[productGroupName] as ProductGroup;
  }

  getProductSchema(datasetName: string, options: EnvironmentVariables, productName: string): Schema {
    return this.schema;
  }

  private getComposedProperties(objectTypeRef: TypeReference, components: TypeReference[]) {
    const result: Property[] = [];
    components.forEach(component => {
      let type = this.schema.getType(component);
      if (!type) {
        const componentTypeDefinition = this.typesMap.get(component);
        if (!componentTypeDefinition) {
          throw new Error(`Components composition error: Cannot find ${component} Type`);
        }
        this.registerType(componentTypeDefinition);
        type = this.schema.getType(component);
      }
      if (type instanceof ObjectType) {
        if (type.components.length > 0) {
          throw new Error(`Wrong type definition for type: ${type.name}. Object cannot contain a component that uses other components`);
        }
        const incompatibleProperty = type.properties.find(componentProperty =>
          result.some(property => isPropertiesIncompatible(property, componentProperty))
        );
        if (incompatibleProperty) {
          throw new Error(`${objectTypeRef} has incompatible property ${incompatibleProperty.name} in ${component} component`);
        }
        type.properties.forEach(property => result.push(property));
      } else {
        throw new Error(`Component ${component} should be an ObjectType`);
      }
    });
    return result;
  }

  private registerType(type: any) {
    // Skip already registered types
    if (this.schema.getType(type.name)) {
      return;
    }
    switch (type.kind) {
      case TypeKind.Enum:
        if (![SystemType.Number, SystemType.String, SystemType.MLString].includes(type.valueType)) {
          throw new Error(`Wrong type definition for type: ${type.name}. 
          Enum type should be on of: '${SystemType.MLString}', '${SystemType.String}', '${SystemType.Number}'`);
        }
        const valueType = type.valueType === SystemType.Number ? 'number' : 'string';
        const invalidValue = type.values.find(v => typeof v !== valueType);
        if (invalidValue) {
          throw new Error(`Wrong type definition for enum: ${type.name}. ${invalidValue} value has incorrect type`);
        }

        this.schema.add(new EnumType(type.name, type.valueType, type.values));
        break;
      case TypeKind.Object:
        if (!Array.isArray(type.properties)) {
          throw new Error(`Wrong type definition for type:${type.name}`);
        }
        let components = [];
        if (type.components) {
          if (!Array.isArray(type.components)) {
            throw new Error(`Wrong type definition for type: ${type.name}. Components should be an array`);
          }

          components = type.components;
        }
        const composed = this.getComposedProperties(type.name, components);
        //TODO: copy externals from component ??
        const externals = type.externals || [];
        const compatibilityTablesDescriptor = new CompatibilityTablesDescriptorParser(type).parse();
        this.schema.add(new ObjectType(
          type.name,
          overrideProperties(
            type.name,
            composed,
            type.properties.map((p: any) =>
              new Property(p.name, p.type, this.mapOpts(type.name, p, this.schema)))
          ),
          components,
          externals,
          compatibilityTablesDescriptor
        ));
        break;
      case TypeKind.Union:
        const types = type.types;
        if (!Array.isArray(types)) {
          throw new Error(`Types in Union ${type.name} should be an array`);
        }
        this.schema.add(new UnionType(type.name, types));
        break;
      case TypeKind.List:
        const nullable = type.nullable;
        if (nullable != null && typeof nullable !== 'boolean') {
          throw new Error(`Wrong type definition for ${type.name}. Nullable should be boolean`);
        }
        this.schema.add(new ListType(type.name, type.elementType, nullable));
        break;
      case TypeKind.Primitive:
        this.schema.add(new PrimitiveType(type.name, type.elementType));
        break;
      default:
        break;
    }
  }

  addOption(option: Option): void {
    const optionId = option._id;
    if (this.optionsRegistry.hasOption(optionId)) {
      throw new Error('Option already exists')
    }
    this.optionsRegistry.addOption(optionId, option);
  }

  updateOption(option: Option): void {
    const origin = this.optionsRegistry.getOptionById(option._id);
    if (!origin) {
      throw new Error('Cannot find option')
    }
    origin.model = option.model;
  }

  createOption(optionDefinition: OptionDefinition): [Option, string[]] {
    const errors: string[] = [];
    let option: Option;
    let pModel: PModel;

    // Parses json to Option
    try {
      const optionRegistry = this.optionsRegistry.clone();
      const parser = new ParsingDataFileVisitor(
        optionRegistry,
        {},
        new OclConstraintRegistry(this.schema),
        new FilterRegistry(this.schema),
        new ExternalParser(this.primitiveValidator, this.propertyPathValidator),
        new ExternalFieldRegistry(this.schema),
        this.schema,
        new Map(),
      )
      parser.visitOption(optionDefinition, false);
      option = optionRegistry.getOptionById(optionDefinition._id);
      this.validateOption(option);
    } catch(e) {
      errors.push(e.message);
    }
    if (!option) {
      return [undefined, errors];
    }
    // Converts Option -> POption
    try {
      const simpleVisitor = new SimpleVisitor(this.schema);
      const traverser = new PrototypeTraverser(this.schema, option.type.typeRef, option.model);
      pModel = traverser.map(simpleVisitor);
    } catch (e) {
      errors.push(e.message);
    }
    // Validates PModel by means of ModelChecker
    const modelChecker = new ModelChecker();
    const validationResult = modelChecker.checkModel(this.schema, pModel, option.type.typeRef);
    if (validationResult.isInvalid) {
      errors.push(validationResult.errorMessage);
    }
    if (errors.length) {
      return [undefined, errors];
    }
    return [option, []];
  }

  validateOption(option: Option): string[] {
    try {
      const simpleVisitor = new SimpleVisitor(this.schema);
      const traverser = new PrototypeTraverser(this.schema, option.type.typeRef, option.model);
      traverser.map(simpleVisitor);
    } catch (e) {
      return [e.message];
    }
    return [];
  }

  validateOptions() {
    this.optionsRegistry.getOptions().forEach((option: Option) => {
      const errors = this.validateOption(option);
      if (errors.length) {
        throw new Error(`Invalid option data "${option._id}". ${errors.join('. ')}`);
      }
    });
  }

  private validateProducts() {
    // throw new Error('Not implemented');
  }

  private mapOpts(typeRef: TypeReference, property, schema: Schema): PropertyOptions {
    const opts = property.opts;
    if (!opts) {
      return {};
    }
    const invalidOpts = Object.keys(opts)
      .filter(o => !['needSource', 'metaOnly', 'constraints', 'availability', 'default', 'checker'].includes(o));
    if (invalidOpts.length) {
      throw new Error(`Wrong type definition of ${typeRef}. Invalid options: ${invalidOpts.join(',')}`);
    }
    let prOpts: PropertyOptions = opts;
    let availability: ExpressionConstraint[] = [];

    if (opts['availability']) {
      for (let av of opts['availability']) {
        let expr = createExpressionConstraint(av, schema);
        if (expr) {
          availability.push(expr);
        }
      }
    }
    prOpts.availability = availability;
    if (prOpts.default != undefined) {

      const propertyType = this.schema.getType(property.type);
      if (propertyType) {
        switch (propertyType.kind) {
          case TypeKind.Primitive:
            prOpts.default = this.primitiveParser.parsePrimitive(propertyType.getTypeReference(), prOpts.default);
            break;
          case TypeKind.Union:
            this.validateDefaultObject(prOpts.default, propertyType.getTypeReference());
            break;
        }
      }
    }
    return prOpts;
  }

  private validateDefaultObject(object: any, typeRef: TypeReference) {
    try {
      const simpleVisitor = new ValidationVisitor(this.schema);
      const traverser = new PrototypeTraverser(this.schema, typeRef, object);
      traverser.map(simpleVisitor);
    } catch (e) {
      throw new Error(`Invalid default object for type reference "${typeRef}". ${e.message}`);
    }
  }

  private parseExternals(type) {
    const typeExternals = type.externals || [];

    typeExternals.forEach(external => {
      const paths = external.properties;
      paths.forEach(path => {
        const pathType = this.propertyPathValidator.parsePropertyType(path, type, external);
        if (!pathType) {
          throw SchemaError.invalidExternalPropertyPath(path, `"${type.name}" of "${external.name}" external field.`);
        }
        const typeRef = pathType.getTypeReference();
        if (pathType.kind !== TypeKind.Primitive && pathType.kind !== TypeKind.Enum) {
          throw SchemaError.invalidExternalPropertyPath(path,
            `"${type.name}" of "${external.name} external field. Property path should reference a primitive type but reference "${typeRef}" instead`);
        }
      });
    });

    type.externals = typeExternals.map(external => this.externalParser.parseDefinition(external, type.name));
  }

  getProductById(id: string): ProductWithContext {
    const product = this.products[id];
    const compatibilityTables = this.compatibilityTables;
    return {
      product: castProduct(product),
      compatibilityTables
    };
  }

  private validateTypeRef(typeRef: TypeReference) {
    if (this.schema.getType(typeRef) === undefined) {
      throw new Error(`Schema doesn't contain target type ${typeRef}`);
    }
  }

  private parseResolutionSchema(loadedSchema: any) {
    if (loadedSchema && loadedSchema['products']) {
      const registeredTypes = new Set(this.typesMap.keys());
      const parser = new ResolutionSchemaParser(loadedSchema, registeredTypes);
      this.schema.resolutionSchemas = parser.parse();
    }
  }

  findOptionGroupById(id: string) {
    return this.optionsRegistry.findOptionGroupById(id);
  }

  getOptions(typeRef: TypeReference): Option[] {
    return this.optionsRegistry.getOptions()
      .filter(option => option.kind === RecordKind.Option && this.isTypeParentFor(typeRef, option.type.typeRef));
  }

  isTypeParentFor(parent: TypeReference, child: TypeReference) {
    if (parent === child) {
      return true;
    }
    const parentType = this.schema.getType(parent);
    if (parentType.kind !== TypeKind.Union) {
      return false;
    }
    const unionParentType = parentType as UnionType;
    return unionParentType.types.some(childType => this.isTypeParentFor(childType, child));
  }

  findOption(id: string) {
    return this.optionsRegistry.getOptionById(id);
  }

  get getAllOptions(): Readonly<Option[]> {
    return this.optionsRegistry.getOptions();
  }

  get datasetList(): Readonly<DatasetList> {
    return this._datasetList;
  }

  applyVisitor(visitor: IDataFileVisitor): void {
    const options = this.optionsRegistry.getOptions();
    options.forEach(option => visitor.visitOption(option));
  }
}
